<template>
  <input type="radio" data-currentPage="11" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.humanInTheLoop', 'Human in the loop')" />
  <div role="tabpanel" data-currentPage="11" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
    <div class="flex justify-between" id="tabTitle">
      <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.title', 'Human in the loop') }}</h1>
    </div>
    <div class="mt-4">
      <div class="form-control w-90 mb-4">
        <label class="label cursor-pointer">
          <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.enabled', 'Enable human handoff') }}</span>
          <input v-model="localEnabled" type="checkbox" class="toggle toggle-accent" />
        </label>
      </div>
      <div class="form-control w-90 mb-4">
        <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.startWebhook', 'Start conversation webhook') }}</p>
        <input v-model="localStartConversationWebhook" type="text" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.humanInTheLoop.startWebhookPlaceholder', 'https://your-omni.example.com/webhooks/handoff/start')" />
        <div class="text-xs mt-2 text-base-content/70 flex items-center flex-wrap">
          <span>{{ t('aiChatAgentSetup.humanInTheLoop.exampleLabel', 'Example') }}: <span class="font-mono bg-base-200 px-1.5 py-0.5 rounded">{{ startWebhookExample }}</span></span>
          <button class="btn btn-ghost btn-xs px-2 min-h-0 h-6 border border-base-300 hover:border-base-content/30 ml-2" @click="copyTextToClipboard(startWebhookExample)">{{ t('aiChatAgentSetup.humanInTheLoop.copyButton', 'Copy') }}</button>
        </div>
      </div>
      <div class="form-control w-90 mb-4">
        <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.newMessageWebhook', 'New message webhook') }}</p>
        <input v-model="localNewMessageWebhook" type="text" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.humanInTheLoop.newMessageWebhookPlaceholder', 'https://your-omni.example.com/webhooks/handoff/message')" />
        <div class="text-xs mt-2 text-base-content/70 flex items-center flex-wrap">
          <span>{{ t('aiChatAgentSetup.humanInTheLoop.exampleLabel', 'Example') }}: <span class="font-mono bg-base-200 px-1.5 py-0.5 rounded">{{ newMessageWebhookExample }}</span></span>
          <button class="btn btn-ghost btn-xs px-2 min-h-0 h-6 border border-base-300 hover:border-base-content/30 ml-2" @click="copyTextToClipboard(newMessageWebhookExample)">{{ t('aiChatAgentSetup.humanInTheLoop.copyButton', 'Copy') }}</button>
        </div>
      </div>
      <div class="form-control w-90 mb-4">
        <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.inboundSecret', 'Inbound webhook secret (optional)') }}</p>
        <input v-model="localInboundSecret" type="text" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.humanInTheLoop.inboundSecretPlaceholder', 'Secret your omni-channel service will send back')" />
      </div>
      <div class="mb-4 w-90">
        <div class="flex justify-between items-center mb-2">
          <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.humanTransferMessage', 'Human transfer message') }}</p>
          <CustomLanguageSelector
            v-model="localSelectedTransferLang"
            :options="languages"
            :placeholder="t('aiChatAgentSetup.humanInTheLoop.languageSelectPlaceholder', 'Select Language')"
            widthClass="w-max min-w-[150px]"
          />
        </div>
        <textarea
          v-model="localTransferMessage"
          class="textarea textarea-bordered w-100"
          style="width: 100%;height: 100px;"
          :style="getDirStyle('humanInTheLoopTransferMessage')"
          :placeholder="t('aiChatAgentSetup.humanInTheLoop.humanTransferMessagePlaceholder', 'Message shown when the conversation is transferred to a human')"
          @keydown="handleDirKeydown($event, 'humanInTheLoopTransferMessage')"
          maxlength="1000"
        ></textarea>
        <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: localTransferMessage.length, max: 1000 }, localTransferMessage.length + '/1000') }}</div>
        <div class="text-xs mt-1 text-base-content/70" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.defaultFallbackHint', 'If only Default is set, it will be used for every language.') }}</div>
      </div>
      <div class="mb-4 w-90">
        <div class="flex justify-between items-center mb-2">
          <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.aiBackMessage', 'AI back message') }}</p>
          <CustomLanguageSelector
            v-model="localSelectedAiBackLang"
            :options="languages"
            :placeholder="t('aiChatAgentSetup.humanInTheLoop.languageSelectPlaceholder', 'Select Language')"
            widthClass="w-max min-w-[150px]"
          />
        </div>
        <textarea
          v-model="localAiBackMessage"
          class="textarea textarea-bordered w-100"
          style="width: 100%;height: 100px;"
          :style="getDirStyle('humanInTheLoopAiBackMessage')"
          :placeholder="t('aiChatAgentSetup.humanInTheLoop.aiBackMessagePlaceholder', 'Message shown when the AI takes the conversation back')"
          @keydown="handleDirKeydown($event, 'humanInTheLoopAiBackMessage')"
          maxlength="1000"
        ></textarea>
        <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: localAiBackMessage.length, max: 1000 }, localAiBackMessage.length + '/1000') }}</div>
        <div class="text-xs mt-1 text-base-content/70" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.defaultFallbackHint', 'If only Default is set, it will be used for every language.') }}</div>
      </div>
      <InfoBox class="w-90 mb-4">
        <div class="w-full">
          <div class="font-semibold">{{ t('aiChatAgentSetup.humanInTheLoop.statusText', 'When handoff starts, the AI will naturally notify the user (e.g. "I am transferring you to a human agent.").') }}</div>
          <div class="opacity-80 mt-1">{{ t('aiChatAgentSetup.humanInTheLoop.examplesHint', 'Use the examples above and copy them directly.') }}</div>
        </div>
      </InfoBox>
      <div class="form-control w-90 mb-4">
        <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.sendMessageEndpoint', 'Send message endpoint (conversationId based)') }}</p>
        <div class="flex items-center w-full">
          <input type="text" :value="sendMessageEndpoint" class="input input-bordered w-full" readonly />
          <button class="btn btn-outline ml-2" @click="copyTextToClipboard(sendMessageEndpoint)">{{ t('aiChatAgentSetup.humanInTheLoop.copyButton', 'Copy') }}</button>
        </div>
      </div>
      <div class="form-control w-90 mb-4">
        <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.humanInTheLoop.returnToAgentEndpoint', 'Return to Agent endpoint (conversationId based)') }}</p>
        <div class="flex items-center w-full">
          <input type="text" :value="returnToAgentEndpoint" class="input input-bordered w-full" readonly />
          <button class="btn btn-outline ml-2" @click="copyTextToClipboard(returnToAgentEndpoint)">{{ t('aiChatAgentSetup.humanInTheLoop.copyButton', 'Copy') }}</button>
        </div>
      </div>
      <div class="form-control w-90 mb-4">
        <button class="btn w-fit" @click="downloadPostman">{{ t('aiChatAgentSetup.humanInTheLoop.downloadPostman', 'Download Postman Collection') }}</button>
      </div>
    </div>
    <div class="flex justify-between mt-4" style="position: relative;">
      <button @click="goPrevious" class="btn">
        <span v-if="isNavigating === 10" class="loading loading-spinner loading-sm mr-2"></span>
        {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
      </button>
      <div class="flex items-center justify-evenly">
        <button v-if="showSaveButton === true" @click="saveWithoutRedirect" class="btn btn-neutral">
          <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
          {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
        </button>
        <button @click="saveAndClose" class="btn btn-accent">
          <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import CustomLanguageSelector from '@/components/CustomLanguageSelector.vue';
import InfoBox from '@/components/InfoBox.vue';

const props = defineProps({
  isRtl: { type: Boolean, required: true },
  t: { type: Function, required: true },
  languages: { type: Array, required: true },
  enabled: { type: Boolean, required: true },
  startConversationWebhook: { type: String, required: true },
  newMessageWebhook: { type: String, required: true },
  inboundSecret: { type: String, required: true },
  selectedTransferLang: { type: String, required: true },
  transferMessage: { type: String, required: true },
  selectedAiBackLang: { type: String, required: true },
  aiBackMessage: { type: String, required: true },
  startWebhookExample: { type: String, required: true },
  newMessageWebhookExample: { type: String, required: true },
  sendMessageEndpoint: { type: String, required: true },
  returnToAgentEndpoint: { type: String, required: true },
  isNavigating: { type: Number, required: true },
  isEditMode: { type: Boolean, required: true },
  showSaveButton: { type: Boolean, required: true },
  isSaving: { type: Boolean, required: true },
  isSavingAndRedirecting: { type: Boolean, required: true },
  copyTextToClipboard: { type: Function, required: true },
  downloadPostman: { type: Function, required: true },
  goPrevious: { type: Function, required: true },
  saveWithoutRedirect: { type: Function, required: true },
  saveAndClose: { type: Function, required: true },
  getDirStyle: { type: Function, required: true },
  handleDirKeydown: { type: Function, required: true }
});

const emit = defineEmits([
  'update:enabled',
  'update:startConversationWebhook',
  'update:newMessageWebhook',
  'update:inboundSecret',
  'update:selectedTransferLang',
  'update:transferMessage',
  'update:selectedAiBackLang',
  'update:aiBackMessage'
]);

const localEnabled = computed({
  get: () => props.enabled,
  set: (value) => emit('update:enabled', value)
});

const localStartConversationWebhook = computed({
  get: () => props.startConversationWebhook,
  set: (value) => emit('update:startConversationWebhook', value)
});

const localNewMessageWebhook = computed({
  get: () => props.newMessageWebhook,
  set: (value) => emit('update:newMessageWebhook', value)
});

const localInboundSecret = computed({
  get: () => props.inboundSecret,
  set: (value) => emit('update:inboundSecret', value)
});

const localSelectedTransferLang = computed({
  get: () => props.selectedTransferLang,
  set: (value) => emit('update:selectedTransferLang', value)
});

const localTransferMessage = computed({
  get: () => props.transferMessage,
  set: (value) => emit('update:transferMessage', value)
});

const localSelectedAiBackLang = computed({
  get: () => props.selectedAiBackLang,
  set: (value) => emit('update:selectedAiBackLang', value)
});

const localAiBackMessage = computed({
  get: () => props.aiBackMessage,
  set: (value) => emit('update:aiBackMessage', value)
});
</script>
